import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, API, formatApiErrorDetail, scaricaFile } from "@/lib/api";
import { euro, num, dateIt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Trash2, Truck, Fuel, Route, CalendarRange, Pencil, FileSpreadsheet } from "lucide-react";
import { VeicoliMultiGiorno } from "@/components/VeicoliMultiGiorno";
import { ModificaVeicoloCosto } from "@/components/ModificaVeicoloCosto";
import { ModificaVeicoliBulk } from "@/components/ModificaVeicoliBulk";

export const VEICOLO_TIPI = { noleggio: "Noleggio", carburante: "Carburante", pedaggio: "Pedaggio" };

export function VeicoliCommessa({ commessaId, onChanged }) {
  const qc = useQueryClient();
  const [inModifica, setInModifica] = useState(null);
  const [selezionate, setSelezionate] = useState([]);
  const [confermaBulk, setConfermaBulk] = useState(false);
  const [modificaBulk, setModificaBulk] = useState(false);
  const [fVeicolo, setFVeicolo] = useState("tutti");
  const [fTipo, setFTipo] = useState("tutti");
  const [fDal, setFDal] = useState("");
  const [fAl, setFAl] = useState("");

  const { data: veicoli = [] } = useQuery({ queryKey: ["veicoli"], queryFn: async () => (await api.get("/veicoli")).data });
  const { data: impostazioni } = useQuery({ queryKey: ["impostazioni"], queryFn: async () => (await api.get("/impostazioni")).data });
  const { data: tutteRighe = [] } = useQuery({
    queryKey: ["veicoli-costi", commessaId],
    queryFn: async () => (await api.get(`/commesse/${commessaId}/veicoli-costi`)).data,
  });
  const rows = tutteRighe
    .filter((r) => fVeicolo === "tutti" || r.veicolo_id === fVeicolo)
    .filter((r) => fTipo === "tutti" || r.tipo === fTipo)
    .filter((r) => !fDal || (r.data || "").slice(0, 10) >= fDal)
    .filter((r) => !fAl || (r.data || "").slice(0, 10) <= fAl);
  const filtriAttivi = fVeicolo !== "tutti" || fTipo !== "tutti" || !!fDal || !!fAl;
  const esportaExcel = () => {
    const p = new URLSearchParams();
    if (fVeicolo !== "tutti") p.set("veicolo_id", fVeicolo);
    if (fTipo !== "tutti") p.set("tipo", fTipo);
    if (fDal) p.set("dal", fDal);
    if (fAl) p.set("al", fAl);
    scaricaFile(`${API}/commesse/${commessaId}/veicoli-costi/export/excel?${p.toString()}`, (m) => toast.error(m));
  };

  const refresh = async () => {
    await qc.refetchQueries({ queryKey: ["veicoli-costi", commessaId], type: "all" });
    qc.invalidateQueries({ queryKey: ["commessa-dash", commessaId] });
    onChanged?.();
  };
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/veicoli-costi/${id}`)).data,
    onSuccess: (_, id) => { setSelezionate((s) => s.filter((x) => x !== id)); refresh(); toast.success("Voce eliminata"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const bulkDelMut = useMutation({
    mutationFn: async (ids) => { for (const id of ids) await api.delete(`/veicoli-costi/${id}`); return ids.length; },
    onSuccess: (n) => { setSelezionate([]); setConfermaBulk(false); refresh(); toast.success(`${n} righe eliminate`); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const totale = rows.reduce((s, r) => s + (r.importo || 0), 0);
  const perTipo = rows.reduce((acc, r) => ({ ...acc, [r.tipo]: (acc[r.tipo] || 0) + (r.importo || 0) }), {});
  const tutteSelezionate = rows.length > 0 && rows.every((r) => selezionate.includes(r.id));
  const toggleTutte = (v) => setSelezionate(v ? rows.map((r) => r.id) : []);
  const toggleRiga = (id) => setSelezionate((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const importoSelezionate = rows.filter((r) => selezionate.includes(r.id)).reduce((s, r) => s + (r.importo || 0), 0);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-5">
      <div className="grid grid-cols-3 gap-3">
        {[
          { k: "noleggio", icon: CalendarRange, label: "Noleggio" },
          { k: "carburante", icon: Fuel, label: "Carburante" },
          { k: "pedaggio", icon: Route, label: "Pedaggi" },
        ].map((t) => (
          <div key={t.k} className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3" data-testid={`vei-kpi-${t.k}`}>
            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500"><t.icon className="h-3.5 w-3.5 text-primary" />{t.label}</div>
            <div className="font-mono font-semibold text-slate-900 mt-1">{euro(perTipo[t.k] || 0)}</div>
          </div>
        ))}
      </div>

      <VeicoliMultiGiorno commessaId={commessaId} veicoli={veicoli} impostazioni={impostazioni} onSaved={refresh} />

      <div className="rounded-lg border border-orange-200 bg-orange-50 px-4 py-3 flex items-center justify-between flex-wrap gap-2" data-testid="vei-totale-box">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{filtriAttivi ? "Totale veicoli filtrato" : "Totale veicoli"}</div>
          <div className="text-xs text-slate-500 mt-0.5" data-testid="vei-totale-descr">
            {[
              fVeicolo !== "tutti" ? tutteRighe.find((r) => r.veicolo_id === fVeicolo)?.veicolo_nome || "—" : "Tutti i veicoli",
              fTipo !== "tutti" && VEICOLO_TIPI[fTipo],
              (fDal || fAl) && `Periodo: ${fDal ? dateIt(fDal) : "…"} → ${fAl ? dateIt(fAl) : "…"}`,
              `${rows.length} righe`,
            ].filter(Boolean).join(" · ")}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="font-mono text-2xl font-semibold text-primary" data-testid="vei-totale-valore">{euro(totale)}</div>
          {true && (
            <Button size="sm" variant="outline" className="h-8" disabled={rows.length === 0} onClick={esportaExcel} data-testid="vei-export-excel">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel {filtriAttivi ? "filtrato" : ""}
            </Button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3" data-testid="vei-filtro-bar">
        <Label className="text-xs text-slate-500">Veicolo</Label>
        <Select value={fVeicolo} onValueChange={setFVeicolo}>
          <SelectTrigger className="h-8 w-[240px]" data-testid="vei-filtro-veicolo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti i veicoli</SelectItem>
            {[...new Map(tutteRighe.map((r) => [r.veicolo_id, r.veicolo_nome])).entries()].map(([vid, nome]) => <SelectItem key={vid} value={vid}>{nome}</SelectItem>)}
          </SelectContent>
        </Select>
        <Label className="text-xs text-slate-500 ml-2">Tipo</Label>
        <Select value={fTipo} onValueChange={setFTipo}>
          <SelectTrigger className="h-8 w-[150px]" data-testid="vei-filtro-tipo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutti">Tutti</SelectItem>
            {Object.entries(VEICOLO_TIPI).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Label className="text-xs text-slate-500 ml-2">Dal</Label>
        <Input type="date" className="h-8 w-[150px]" value={fDal} onChange={(e) => setFDal(e.target.value)} data-testid="vei-filtro-dal" />
        <Label className="text-xs text-slate-500">Al</Label>
        <Input type="date" className="h-8 w-[150px]" value={fAl} onChange={(e) => setFAl(e.target.value)} data-testid="vei-filtro-al" />
        {filtriAttivi && (
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setFVeicolo("tutti"); setFTipo("tutti"); setFDal(""); setFAl(""); }} data-testid="vei-filtro-reset">Azzera filtri</Button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="text-center py-10 text-slate-400 border border-dashed border-slate-200 rounded-lg" data-testid="vei-empty">
          <Truck className="h-8 w-8 mx-auto mb-2 opacity-40" />{filtriAttivi ? "Nessun costo veicolo con i filtri attivi." : "Nessun costo veicolo registrato."}
        </div>
      ) : (
        <div className="space-y-2">
          {selezionate.length > 0 && (
            <div className="flex items-center justify-between flex-wrap gap-2 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm" data-testid="vei-bulk-bar">
              <span><b>{selezionate.length}</b> righe selezionate · {euro(importoSelezionate)}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" className="h-8" onClick={() => setSelezionate([])} data-testid="vei-bulk-deseleziona">Deseleziona</Button>
                <Button size="sm" className="h-8" onClick={() => setModificaBulk(true)} data-testid="vei-bulk-modifica">
                  <Pencil className="h-4 w-4 mr-1.5" /> Modifica selezionati
                </Button>
                <Button size="sm" className="h-8 bg-red-600 hover:bg-red-700 text-white" onClick={() => setConfermaBulk(true)} data-testid="vei-bulk-elimina">
                  <Trash2 className="h-4 w-4 mr-1.5" /> Elimina selezionati
                </Button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                  <th className="py-2 px-3 w-8"><Checkbox checked={tutteSelezionate} onCheckedChange={toggleTutte} aria-label="Seleziona tutto" data-testid="vei-seleziona-tutto" /></th>
                  <th className="py-2 px-3">Data</th><th className="py-2 px-3">Veicolo</th><th className="py-2 px-3">Tipo</th><th className="py-2 px-3">Dettaglio</th><th className="py-2 px-3 text-right">Importo</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={`border-b border-slate-50 ${selezionate.includes(r.id) ? "bg-orange-50/60" : ""}`} data-testid={`vei-row-${r.id}`}>
                    <td className="py-2.5 px-3"><Checkbox checked={selezionate.includes(r.id)} onCheckedChange={() => toggleRiga(r.id)} data-testid={`vei-check-${r.id}`} /></td>
                    <td className="py-2.5 px-3 text-slate-700">{dateIt(r.data)}</td>
                    <td className="py-2.5 px-3 text-slate-700">{r.veicolo_nome}</td>
                    <td className="py-2.5 px-3"><span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-primary border border-orange-200">{VEICOLO_TIPI[r.tipo]}</span></td>
                    <td className="py-2.5 px-3 text-slate-500 text-xs">
                      {r.tipo === "noleggio" && `${num(r.giorni)} g × ${euro(r.costo_giornaliero)}`}
                      {r.tipo === "carburante" && `${r.km_iniziali != null && r.km_finali != null ? `${num(r.km_iniziali)}→${num(r.km_finali)} km · ` : ""}${num(r.km)} km · ${num(r.litri, 2)} l × ${euro(r.costo_litro)}`}
                      {r.descrizione ? ` · ${r.descrizione}` : ""}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono font-medium">{euro(r.importo)}</td>
                    <td className="py-2.5 px-3 text-right whitespace-nowrap">
                      <button onClick={() => setInModifica(r)} className="text-slate-300 hover:text-primary mr-3" title="Modifica" data-testid={`edit-vei-row-${r.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => delMut.mutate(r.id)} className="text-slate-300 hover:text-red-600" data-testid={`delete-vei-row-${r.id}`}><Trash2 className="h-4 w-4" /></button>
                    </td>
                  </tr>
                ))}
                <tr className="font-semibold"><td colSpan={5} className="py-2.5 px-3">Totale veicoli</td><td className="py-2.5 px-3 text-right font-mono" data-testid="vei-totale">{euro(totale)}</td><td></td></tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ModificaVeicoloCosto riga={inModifica} veicoli={veicoli} onClose={() => setInModifica(null)} onSaved={() => { setInModifica(null); refresh(); }} />

      <ModificaVeicoliBulk open={modificaBulk} ids={selezionate} onClose={() => setModificaBulk(false)} onSaved={() => { setModificaBulk(false); setSelezionate([]); refresh(); }} />

      <AlertDialog open={confermaBulk} onOpenChange={setConfermaBulk}>
        <AlertDialogContent data-testid="vei-bulk-conferma">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Elimina {selezionate.length} righe costo veicolo</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminate definitivamente {selezionate.length} righe per un totale di {euro(importoSelezionate)}. L'operazione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="vei-bulk-annulla">Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" disabled={bulkDelMut.isPending} onClick={(e) => { e.preventDefault(); bulkDelMut.mutate(selezionate); }} data-testid="vei-bulk-conferma-ok">
              {bulkDelMut.isPending ? "Eliminazione…" : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
